import { ChevronRight } from 'lucide-react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { IconBuildingWarehouse, IconBook, IconUsers } from '@tabler/icons-react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from '@/components/ui/sidebar';

import { NavSection, PAGES, type PageType } from 'constants/pages';
import { transformForComparison } from 'helpers/textHelper';

const NAV_SECTION_SHORT_LABELS: Record<NavSection, string> = {
    [NavSection.WarehouseManagementSystem]: 'WMS',
    [NavSection.CatalogManagementSystem]: 'CMS',
    [NavSection.CustomerManagementSystem]: 'Customer',
};

const NAV_SECTION_ICONS = {
    [NavSection.WarehouseManagementSystem]: IconBuildingWarehouse,
    [NavSection.CatalogManagementSystem]: IconBook,
    [NavSection.CustomerManagementSystem]: IconUsers,
};

function getNavItems() {
    return Object.values(NavSection).map((section) => {
        const pages = Object.values(PAGES)
            .filter((page) => page.navSection === section)
            .sort((a, b) => (a.shortLabel ?? a.label).localeCompare(b.shortLabel ?? b.label));

        return {
            section,
            label: NAV_SECTION_SHORT_LABELS[section],
            Icon: NAV_SECTION_ICONS[section],
            pages,
        };
    });
}

export function NavMain({ search }: { search: string }) {
    const location = useLocation();
    const query = transformForComparison(search.trim());
    const navItems = getNavItems()
        .map((item) => ({
            ...item,
            pages: item.pages.filter((page) =>
                [page.label, page.shortLabel].some((text) => text && transformForComparison(text).includes(query)),
            ),
        }))
        .filter(({ pages }) => pages.length > 0);

    return (
        <>
            {navItems.map(({ section, label, Icon, pages }) => (
                <SidebarGroup key={section}>
                    <SidebarGroupLabel>{label}</SidebarGroupLabel>
                    <SidebarMenu>
                        {/* Remounted when a search starts or ends, so a collapsed section reopens on its matches */}
                        <Collapsible
                            key={query ? 'search' : 'browse'}
                            asChild
                            defaultOpen
                            className="group/collapsible"
                        >
                            <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton tooltip={label}>
                                        <Icon />
                                        <span>{label}</span>
                                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        {pages.map((page: PageType) => {
                                            const isActive = !!matchPath(location.pathname, page.url);
                                            return (
                                                <SidebarMenuSubItem key={page.url}>
                                                    <SidebarMenuSubButton asChild isActive={isActive}>
                                                        <Link to={page.url}>
                                                            {page.icon}
                                                            <span>{page.shortLabel ?? page.label}</span>
                                                        </Link>
                                                    </SidebarMenuSubButton>
                                                </SidebarMenuSubItem>
                                            );
                                        })}
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            </SidebarMenuItem>
                        </Collapsible>
                    </SidebarMenu>
                </SidebarGroup>
            ))}
        </>
    );
}
